import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';
import { getDate, getTimestamp } from '../utils/utilities';
import useImperativeIntl from '../../../hooks/useImperativeIntl';

export const TimeSeriesContext = createContext({
  isPlaying: false,
  setIsPlaying: (value) => {},
  onPlay: () => {},
  isPaused: false,
  setIsPaused: (value) => {},
  onPause: () => {},
  onStop: () => {},
  timeWindow: [],
  setTimeWindow: (value) => {},
  onTimeWindowUpdate: (value) => {},
  togglePlay: () => {},
  stop: () => {},
  intl: {}
});

export function useTimeSeriesContext() {
  return useContext(TimeSeriesContext);
}

export function TimeSeriesProvider({
  children,
  isPlaying,
  isPaused,
  onPlay,
  onPause,
  onStop,
  timeWindow,
  onTimeWindowUpdate,
  intl
}) {
  const [_isPlaying, setIsPlaying] = useState(isPlaying);
  const [_isPaused, setIsPaused] = useState(isPaused);
  const [_timeWindow, setTimeWindow] = useState([]);
  const intlConfig = useImperativeIntl(intl);

  useEffect(() => {
    const sorted = _timeWindow
      .sort((timeA, timeB) => (timeA < timeB ? -1 : 1))
      .map(getDate);
    onTimeWindowUpdate?.(sorted);
  }, [_timeWindow, onTimeWindowUpdate]);

  const togglePlay = useCallback(() => {
    if (_isPlaying) {
      if (onPause) onPause();
      setIsPaused(true);
    }
    if (!_isPlaying) {
      if (onPlay) onPlay();
      setIsPaused(false);
    }
    setIsPlaying((oldState) => !oldState);
  }, [_isPlaying, onPause, onPlay]);

  const stopWrapper = useCallback(() => {
    setIsPlaying(false);
    setIsPaused(false);
    setTimeWindow([]);
  }, []);

  const stop = useCallback(() => {
    stopWrapper();
    // Wait a clock cycle to avoid condition race
    // meanwhile the states is setted above
    setTimeout(onStop || (() => {}));
  }, [onStop, stopWrapper]);

  // These four useEffects are used to allow
  // manage time series state outside the widget
  useEffect(() => {
    if (isPlaying !== _isPlaying) {
      setIsPlaying(isPlaying);
    }
    // Only when the state out of the context changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying]);

  useEffect(() => {
    if (isPaused !== _isPaused) {
      setIsPaused(isPaused);
    }
    // Only when the state out of the context changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaused]);

  useEffect(() => {
    if (!timeWindow) return;

    const timeWindowTimestamp = timeWindow.map(getTimestamp);
    const _timeWindowTimestamp = _timeWindow.map(getTimestamp);
    if (
      timeWindowTimestamp[0] !== _timeWindowTimestamp[0] ||
      timeWindowTimestamp[1] !== _timeWindowTimestamp[1]
    ) {
      setTimeWindow(timeWindowTimestamp);
    }
    // Only when the state out of the context changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, timeWindow);

  return (
    <TimeSeriesContext.Provider
      value={{
        isPlaying: _isPlaying,
        setIsPlaying,
        isPaused: _isPaused,
        setIsPaused,
        timeWindow: _timeWindow,
        setTimeWindow,
        onTimeWindowUpdate,
        togglePlay,
        stop,
        intl: intlConfig
      }}
    >
      {children}
    </TimeSeriesContext.Provider>
  );
}
